import { setupCloseButton } from "./close";
import { hideCpfForm, setupCpfForm } from "./cpf-form";
import { hideError, showError, showErrorTitleAndDescription } from "./error";
import { showSpinner } from "./loading";
import { hideConfirmPayment } from "./payment";
import { setupPrivacy } from "./privacy";
import { setupQrForm } from "./qr-form";
import { setupTerms } from "./terms";
import { setupTexts } from "./texts";
import { showThanks } from "./thanks";
import { getAnalyticsUrl, getProxyUrl } from "./utils";

const SESSION_URL = `${getProxyUrl()}/session/`;

const HEADER_CSRF_TOKEN = "X-Csrf-Token";
const HEADER_HASH = "X-Hash";
const HEADER_SESSION_REF = "X-Session-Ref";
const HEADER_CORRELATION_ID = "X-Correlation-ID";
const HEADER_CURRENCY = "X-Currency";
const HEADER_CENTS = "X-Cents";
const HEADER_LOCALE = "X-Locale";
const HEADER_MERCHANT = "X-Merchant-Ref";
const HEADER_SITE = "X-Merchant-Site";
const HEADER_USER_ID = "X-User-Id";
const HEADER_CUSTOM_RETURN_URL = "X-Custom-Return-Url";

const POLLING_INTERVAL = 1000;

let contentHash = "";
let csrfToken = "";
let sessionId = "";
let bankSessionRef = "";
let bankFlowId = "";
let locale = "pt";
let fourNineNineRetries = 0;
let customReturnUrl = "";

let shouldRedirect = true;
let fullstoryIdentified = false;

const domain = document.location.host.split(":")[0];
const environment = (() => {
  if (domain.match(/staging/)) {
    return "staging";
  }
  if (domain.match(/sandbox/)) {
    return "sandbox";
  }
  if (domain.match(/localhost/)) {
    return "localhost";
  }
  return "production";
})();

export const sendAnalyticsHit = async (
  sessionId: string,
  feedback?: {
    reason: string;
    details: string;
  },
  fs_id?: string,
) => {
  const reqBod = {
    session_id: sessionId,
    fs_id,
    url: window.location.href,
    feedback,
  };
  // not part of the cors response from mantis

  const headers = new Headers();
  headers.set(HEADER_CSRF_TOKEN, csrfToken);
  headers.set(HEADER_CORRELATION_ID, getCorrelationId(sessionId));
  headers.set("Content-Type", "application/json");

  fetch(getAnalyticsUrl(sessionId), {
    method: "POST",
    headers,
    body: JSON.stringify(reqBod),
  });
};

const setDatadogCorrelationId = () => {
  try {
    (window as any).DD_LOGS.onReady(() => {
      (window as any).DD_LOGS.setGlobalContextProperty("correlation-id", sessionId);

      console.log("datadog loaded");
    });
  } catch (e) {
    console.error("failed to set correlation id in datadog", e);
  }
};

const catchProbablePullToRefresh = () => {
  let touchstartY = 0;

  const touchstart = (e: TouchEvent) => {
    touchstartY = e.touches[0].clientY;
  };

  const touchmove = (e: TouchEvent) => {
    const touchY = e.touches[0].clientY;
    const touchDiff = touchY - touchstartY;

    if (touchDiff > 10 && window.scrollY === 0) {
      console.warn("probable pull-to-refresh");

      document.removeEventListener("touchstart", touchstart);
      document.removeEventListener("touchmove", touchmove);
    }
  };

  document.addEventListener("touchstart", touchstart);
  document.addEventListener("touchmove", touchmove);

  //

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      console.log("site back in focus");
    }
  });
};

const setupErrorFormTryAgain = () => {
  document.getElementById("t-e-b2")?.addEventListener("click", async () => {
    hideError();
    showSpinner();

    const headers = generateHeaders();

    await fetch(generateUrl(), {
      method: "POST",
      headers: headers,
    });

    contentHash = "";

    await get();
  });
};

const setupCpfButton = () => {
  const cpfInputElement = document.getElementById("cpf-input") as HTMLInputElement;

  document.getElementById("cpf-submit")?.addEventListener("click", async () => {
    showSpinner();
    hideCpfForm();

    const headers = generateHeaders();

    const response = await fetch(generateUrl(), {
      method: "POST",
      headers: headers,
      body: JSON.stringify({
        data: { form_identifier: "cpf-form", data: [{ key: "pno", value: cpfInputElement.value }] },
      }),
    });

    const r = await response.json();

    bankFlowId = r?.content?.flow_id ?? bankFlowId;

    contentHash = "";

    await get();
  });
};

export const start = async (s: string) => {
  sessionId = s;

  try {
    (window as any).FS("setProperties", {
      type: "page",
      properties: {
        broker_session_id: sessionId,
        environment,
        country_code: "BR",
      },
    });
    (window as any).FS("observe", {
      type: "start",
      callback: () => {
        const fs_id = (window as any).FS("getSession", {
          format: "id",
        });
        // do not await:
        sendAnalyticsHit(sessionId, undefined, fs_id);
      },
    });
  } catch (e) {
    console.error("failed to set fullstory page properties", e);
  }

  if (!sessionId) {
    console.error("no session id");
    showError(locale);
    return;
  }

  console.log("starting session", sessionId);

  setDatadogCorrelationId();

  catchProbablePullToRefresh();

  // Setup all terms links
  setupTerms();

  // Setup all privacy links
  setupPrivacy();

  // Setup error form try again
  setupErrorFormTryAgain();

  // Setup cpf submit button
  setupCpfButton();

  await get();
};

const uuidv4 = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c == "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const generateHeaders = () => {
  const headers = new Headers();
  headers.set(HEADER_CSRF_TOKEN, csrfToken);
  headers.set(HEADER_HASH, contentHash);
  headers.set(HEADER_CORRELATION_ID, getCorrelationId(sessionId));

  return headers;
};

const getCorrelationId = (sid: string) => `${sid}-${uuidv4()}`;
const isFlowFinished = (c: any) => c && c.content && c.content.finished == true;
const isTransferFinished = (c: any) => c && c.content && c.content.transfer && c.content.transfer.state === "FINISHED";
const isQrForm = (c: any) => c && c.content && c.content.form && c.content.form.form_identifier === "qr-form";
const isFormError = (c: any) => c && c.content && c.content.form && c.content.form.form_identifier === "FORM_ERROR";
const isCpfForm = (c: any) => c && c.content && c.content.form && c.content.form.form_identifier === "cpf-form";

const handleFinishedFlow = async () => {
  showSpinner();

  // Poke transfer endpoint to see how the transfer went
  const headers = generateHeaders();

  const response = await fetch(generateUrl(true), {
    method: "GET",
    headers: headers,
  });

  const r = await response.json();

  if (isTransferFinished(r)) {
    showThanks(shouldRedirect, customReturnUrl);
    return;
  }

  // Guess we failed then
  showError(locale, r);
};

const handleReply = async (response: any): Promise<boolean> => {
  if (isFlowFinished(response)) {
    // We need to still ask how the transfer went to please Mantis
    await handleFinishedFlow();
    return false;
  } else if (isQrForm(response)) {
    hideCpfForm();
    setupQrForm(response.content.form, locale);
    return true;
  } else if (isCpfForm(response)) {
    hideConfirmPayment();
    setupCpfForm(response.content.form);
    return false;
  } else if (isFormError(response)) {
    const content = response.content.form.elements.find(
      (e: any) => e.type == "context" && e.tags.includes("BOX_WARNING"),
    ).content;

    if (content.length == 0) {
      showError(locale);
      return false;
    }

    const title = content.length === 1 ? "" : content[0].content;
    const description = content.length === 1 ? content[0].content : content[1].content;

    showErrorTitleAndDescription(title, description, true);
    return false;
  } else if (response && response.content && response.content.poll == true) {
    // happens on when posting form etc.
    return true;
  }

  // We failed or couldn't handle the answer, show an error
  showError(locale, response);
  return false;
};

const generateUrl = (checkTransfer?: boolean) => {
  if (checkTransfer) {
    return `${SESSION_URL}${sessionId}/${bankSessionRef}/${bankFlowId}/transfer`;
  }

  if (bankSessionRef && bankFlowId) {
    return `${SESSION_URL}${sessionId}/${bankSessionRef}/${bankFlowId}`;
  }

  return SESSION_URL + sessionId;
};

const setMerchantAndSite = (merchant: string, site: string) => {
  const merchantClasses: Record<string, string[]> = {
    novatech: ["novatech", "novatech-solutions"],
    xpgames: ["xpgames"],
    clearpay: ["sftg-limited", "clearpay"],
  };

  const siteClasses: Record<string, string[]> = {
    qbet: ["https://qbet.com/", "dev.qbet.com", "Qbet"],
    betsson: ["qa.betsson.com", "betsson.com"],
  };

  const htmlElement = document.querySelector("html");
  if (htmlElement) {
    for (const key of Object.keys(merchantClasses)) {
      if (merchantClasses[key].includes(merchant)) {
        htmlElement.classList.add(`merchant-${key}`);
        break;
      }
    }

    for (const key of Object.keys(siteClasses)) {
      if (siteClasses[key].includes(site)) {
        htmlElement.classList.add(`site-${key}`);
        break;
      }
    }
  }

  shouldRedirect = !["xpgames"].includes(merchant);

  setupCloseButton(sessionId, shouldRedirect, locale, customReturnUrl);
};

const get = async () => {
  // console.log('getting data from proxy', sessionId);

  const headers = generateHeaders();

  try {
    const response = await fetch(generateUrl(), {
      method: "GET",
      headers: headers,
    });

    if (response.status == 204) {
      // console.log('no content');
      setTimeout(get, POLLING_INTERVAL);
      return;
    } else if (response.status == 499) {
      if (fourNineNineRetries < 3) {
        fourNineNineRetries++;
        console.warn("got 499, scheduling a retry");
        setTimeout(get, POLLING_INTERVAL);
        return;
      } else {
        showError(locale);
        return;
      }
    }

    const r = await response.json();

    const h = response.headers;

    if (!fullstoryIdentified && h.get(HEADER_USER_ID)) {
      fullstoryIdentified = true;
      try {
        (window as any).FS("setIdentity", {
          uid: h.get(HEADER_USER_ID),
        });
      } catch (e) {
        console.error("failed to set fullstory identity", e);
      }
    }

    contentHash = h.get(HEADER_HASH) ?? contentHash;
    if (h.get(HEADER_CSRF_TOKEN)) {
      csrfToken = h.get(HEADER_CSRF_TOKEN) || "";
      sendAnalyticsHit(sessionId);
    }
    bankSessionRef = h.get(HEADER_SESSION_REF) ?? bankSessionRef;
    customReturnUrl = h.get(HEADER_CUSTOM_RETURN_URL) ?? customReturnUrl;
    bankFlowId = r?.content?.flow_id ?? bankFlowId;

    // If we get headers for locale, currency and amount, update texts
    if (["pt", "en"].includes(h.get(HEADER_LOCALE) || "pt")) {
      locale = h.get(HEADER_LOCALE) || "pt";
    }
    const amount = h.get(HEADER_CENTS);
    const currency = h.get(HEADER_CURRENCY);
    if (locale && amount && currency) {
      setupTexts(locale, currency, amount);
    }

    // Set some themeing
    const merchant = h.get(HEADER_MERCHANT);
    const site = h.get(HEADER_SITE);
    if (merchant && site) {
      setMerchantAndSite(merchant, site);
    }

    const shouldPoll = await handleReply(r);
    if (shouldPoll) {
      // Should poll again
      setTimeout(get, POLLING_INTERVAL);
    }
  } catch (error) {
    console.error("failed to get", error);
    setTimeout(get, POLLING_INTERVAL);
  }
};
